/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2018, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'./DataItem',
	'../../api/DataItemAPI',
	'../../visualizations/interactions/BinningActionsUtils',
	'../../util/TransactionUtil',
	'../../apiHelpers/SlotAPIHelper'
], (DataItem, DataItemAPI, BinningActionsUtils, TransactionUtil, SlotAPIHelper) => {
	class MappedDataItem extends DataItem {
		constructor(filterSupport, dataItemModel, slot, dataSource, transaction, locale, visualizationImpl, slotsImpl) {
			super(dataItemModel, dataSource, transaction, locale);
			this.visualizationImpl = visualizationImpl;
			this.slotsImpl = slotsImpl;
			this.localFilters = filterSupport.getLocalFilters();
			this.filterSupport = filterSupport;
			this.slot = slot;
		}

		getSlot() {
			return this.slot;
		}

		destroy() {
			super.destroy();
			this.visualizationImpl = null;
			this.slotsImpl = null;
			this.localFilters = null;
			this.filterSupport = null;
		}

		setSlot(slot, transactionToken) {
			this.slot = slot;
			if(slot && this.getBinning()) {
				const canApplyBinning = this.visualizationImpl.getDefinition().getProperty('canApplyBinning');
				if(!canApplyBinning || !SlotAPIHelper.doesDataItemSupportBinning(slot, this.getAPI())) {
					this.getAPI().setBinning(null, transactionToken);
				}
			}
		}

		getType() {
			let slotType;
			if (this.slot) {
				const def = this.slot.getDefinition();
				if ( def.getSubType() === 'latitude' || def.getSubType() === 'longitude') {
					slotType = 'category';
				} else if (def.getType() === 'any') {
					if (SlotAPIHelper.isMultiMeasuresSeriesSlot(this.slot)) {
						slotType = 'category';
					} else if ( this.getBinning()) {
						slotType = 'category';
					} else {
						const metadata = this.getMetadataColumn();
						if (metadata && metadata.getType() === 'attribute') {
							if (this.getAggregation() === DataItemAPI.AGGREGATION_TYPE.none) {
								slotType = 'category';
							} else {
								slotType = 'ordinal';
							}
						} else if (metadata && metadata.getType()  === 'fact') {
							/**
							 * - If one of the items in an 'any' slot is a measure, and we have more than one items,
							 * then simply consider slot type to be category.
							 * This prevents issues described in defect #255087
							 */
							if (this.slot.getDataItemList().length > 1  && !this.slot.getDefinition().getProperty('repeats')) {
								slotType = 'category';
							} else {
								slotType = 'ordinal';
							}
						}
					}
				}
				if (!slotType) {
					slotType = def.getType();
				}
			}
			return slotType && (slotType === 'ordinal' ? 'fact' : 'attribute');
		}

		setAggregation(aggregationType, transactionToken) {
			if (!this.slot){
				super.setAggregation(aggregationType, transactionToken);
			} else {
				const subTransaction = this.transaction.startTransaction(transactionToken);
				if (this.getType() === 'fact') {
					let currentAggregationType = this.getAggregation();
					let localFilters = this.localFilters;
					let rangeFilterOnOldAggregationType = localFilters.getFilterEntry(localFilters.getOrdinalFilterKey(this.getColumnId(), currentAggregationType));
					if (rangeFilterOnOldAggregationType) {
						let itemContext = {
							uniqueId: this.getId(),
							itemId: this.getColumnId(),
							itemName: this.getLabel(),
							dataType: this.getDataType(),
							sourceId: this.visualizationImpl.getDataSource().getId(),
							aggregationType: currentAggregationType
						};
						this.visualizationImpl.getLocalFilters().removeFilter(itemContext, subTransaction);
					}
				}
				super.setAggregation(aggregationType, subTransaction);
				this.transaction.endTransaction(subTransaction);
			}
		}

		getAggregation() {
			if (!this.slot){
				return super.getAggregation();
			}

			const mdColumn = this.getMetadataColumn();
			if (!mdColumn) {
				return null;
			}

			let type = this.dataItemModel.aggregate;
			if (!type){

				// TODO -- livewidget_cleanup --- apply this old code in here (used to be in visDataSlotsManager)
				// Basically if the category is in both measure and  category slot, set the default aggregation to Count.. instead of

				const mappedSlots = this.slotsImpl.getMappedSlotList();

				// Check to see if we have the same column in both the aggregations and dimensions, if so, default to aggType of 'count'
				if (this.slot.getDefinition().getType() === 'ordinal' && this.getMetadataColumn().getType() === 'attribute') {
					var itemId = this.getColumnId();
					for (let i = 0 ; i < mappedSlots.length; i++){
						const slotAPI = mappedSlots[i];
						const slotDef = slotAPI.getDefinition();
						if (slotDef.getType() === 'category' || slotDef.getType() === 'any') {
							var dataItemAPIs = slotAPI.getDataItemList();
							for (let j = 0 ; j < dataItemAPIs.length; j++){
								if (dataItemAPIs[j].getColumnId() === itemId) {
									type = 'count';
									break;
								}
							}
						}
						// Stop once aggType is set.
						if (type) {
							break;
						}
					}
				}

				if (!type){
					const mdColumn = this.getMetadataColumn();
					type = mdColumn ? mdColumn.getDefaultAggregation() : null;
				}
			}

			const def = this.slot.getDefinition();
			if( def.getType() === 'any') {
				if (super.getType() === 'attribute') {
					if( !super.hasDefaultAggregation() ) {
						return type;
					} else {
						type = DataItemAPI.AGGREGATION_TYPE.none;
					}
				}
			// fact assigned to a category slot should not aggregate
			} else if(def.getType() === 'category') {
				type =  DataItemAPI.AGGREGATION_TYPE.none;
			// when an attribute is used in an ordinal slot, aggregation none doesn't make sense...consider it a count
			} else if(def.getType() === 'ordinal' && super.getType() === 'attribute' && type === DataItemAPI.AGGREGATION_TYPE.none) {
				const defaultAggrType = super.getMetadataColumn().getDefaultAggregation();
				type = defaultAggrType === DataItemAPI.AGGREGATION_TYPE.none ? DataItemAPI.AGGREGATION_TYPE.countDistinct : defaultAggrType;
			}
			return type;
		}

		/**
		 * Todo: Ideally we should move the logic to isDateOrTimeType method of MetadataColumn. 
		 * However, we don't want to take the risk at the moment until we are clear about why isDateOrTimeType implementation
		 * of MetadataColumn does not consider taxonomyList.
		 * There are other places need to re-visit after the move:
		 * eg. ChartInsightsQueryParams.js in explore, Forecast.js and SmartsExecution.js in liveWidget
		 * Also, mamke sure this change won't break PA.		 
		 * */
		_isRepresentDate(){
			const representsDateTime = this._getRepresentDateType();

			if (representsDateTime) {
				if (representsDateTime === 'date' || representsDateTime === 'datetime') {
					const metadataColumn = this.getMetadataColumn();
					const dataType = metadataColumn.getDataType();
					return dataType === 'date' || dataType === 'datetime';
				}

				return true;
			}

			return false;
		}

		_getRepresentDateType(){
			const metadataColumn = this.getMetadataColumn();
			if (!metadataColumn) { return null; }
			if (metadataColumn.isDateTimeDataType()){
				return metadataColumn.getDataType();
			}else{
				const dateTypesMap = {
					'cDate':'date',
					'cYear': 'year',
					'cDateTime':'datetime',
					'cTime': 'time'
				};
				const taxonomyList = metadataColumn.getTaxonomyList  && metadataColumn.getTaxonomyList() || [];
				let isTaxonmomyDateType = taxonomyList.length
					&& taxonomyList[0].getClass() === 'cTime'
					&& dateTypesMap[taxonomyList[0].getFamily()] !== undefined ;
				return isTaxonmomyDateType ? dateTypesMap[taxonomyList[0].getFamily()]:null;
			}
		}

		/**
		* @function MappedDataItem#getFormat
		* @description Retrieve the format for the mapped data item.
		* @return {Object} An object containing format related properties.
		*/
		getFormat() {

			if (!this.slot){
				return super.getFormat();
			}

			const widgetModel = this.visualizationImpl.widgetModel;

			// this comes from an upgraded dashboard where we want to change the KPI format. see SummaryKPIUpgrade.js
			const useSlotDefinitionFormat = widgetModel.enableSlotDefinitionFormat === false ? false : true;
			const slotDefinitionFormat = this._getDefinitionFormat();

			let format = super.getFormat();
			if (useSlotDefinitionFormat && !format) {
				format = slotDefinitionFormat;
			}

			if (!format && (this.getMetadataColumn().getType() !== 'attribute' || this._isRepresentDate())) {
				// Default format
				format = {
					'selectedFormat': 'none',  // This property does not appear to be used anymore, it may be replaced with dateStyle
					'maximumFractionDigits': 2,
					'formatLength': 'short'  // This property is only applicable for dates
				};
				if (this._isRepresentDate()) {
					format.type = this._getRepresentDateType();
					format.formatLength = format.type === 'year' ? 'full' : 'short';
				}
			}

			// resolve the proper format when have an invalid format
			const metadataColumn = this.getMetadataColumn();
			if (format && metadataColumn){
				let isDateOrTimeType = this._isRepresentDate();
				if (isDateOrTimeType) {
					isDateOrTimeType =  this.getType() !== 'ordinal';
				}
				const dateTypes = ['date', 'time', 'datetime', 'year'];
				const formatType = format && format.type;
				const invalidDateFormatting = isDateOrTimeType &&  metadataColumn.getType() !== 'attribute' && dateTypes.indexOf(formatType) === -1;
				const invalidBinningFormatting = this.getBinning() && (formatType === 'currency' || formatType === 'percent');
				if (invalidDateFormatting || invalidBinningFormatting || !isDateOrTimeType && dateTypes.indexOf(formatType) > -1) {
					format = {
						'selectedFormat': 'none',
						'maximumFractionDigits': 2,
						'formatLength': 'short',
						'locale': this.locale
					};
					if (invalidDateFormatting || invalidBinningFormatting) {
						format.type = metadataColumn.getDataType();
					}
				}
			}

			// Overwrite the format type to be number if the dataItem is fact and doesn't already have a number format spec set
			if (this.getType() === 'fact') {
				if (super.isDateOrTimeType() && format && format.type !== 'number') {
					// Default format
					return {
						'selectedFormat': 'none',
						'maximumFractionDigits': 2,
						'formatLength': 'short',
						'type': 'number',
						'locale': this.locale
					};
				} else if (!format && (super.getAggregation()==='count' || super.getAggregation()==='countdistinct')) {
					//If the item is assigned to an ordinal slot and it has aggregation type count or countdistinct and no format specified,
					//return a default (1 decimal number)
					//this should be changed back to 0 when implementation of VIDA story for using tickInterval is finished. See story 228160
					return {
						'selectedFormat': 'none',
						'maximumFractionDigits': 1,
						'formatLength': 'short',
						'type': 'number',
						'locale': this.locale
					};
				}
			}

			// TODO -- livewidget_cleanup - we should not add .selectedFormat to set the ui to auto. We should simply call the hasDefaultFormat() API
			// ensure use auto if using default format
			if (format && super.hasDefaultFormat()) {
				format.selectedFormat = 'none';
			}

			if (format && !format.locale) {
				format.locale = this.locale;
			}

			return format;
		}

		_getDefinitionFormat() {
			let definitionFormat = null;
			let format = this.slot.getDefinition().getFormat();
			if (format) {
				definitionFormat = Object.assign({ locale: this.locale }, format);
			}
			return definitionFormat;
		}


		/**
		 * Updates the priority field on any sort entries on all slots, such that they enforce priority order as implied by the slot definition order.
		 * This is done before a new sort is added, and the events are silent, as the sort addition will send a non-silent event to trigger render.
		 * @param {string} slotId - the id of the slot having sort added
		 * @param {string} dataItemId - the id of the data item having sort added
		 * @param {number} explicitPriority (optional) - the priority explicitly given to the data item.
		 * 					If omitted, the priority will be assigned automatically based on the existing data item sorts
		 * @param {object} options
		 */
		_setSortPriorities(slotId, dataItemId, explicitPriority) {
			const isExplicitPriority = typeof explicitPriority !== 'undefined' && explicitPriority !== null;
			let priority = 0, newPriority;
			const mappedSlotList = this.slotsImpl.getMappedSlotList();
			mappedSlotList.forEach((slot) => {
				const currentSlotId = slot.getId();
				const dataItemList = slot.getDataItemList();
				dataItemList.forEach((dataItem)=>{
					if (slotId === currentSlotId && dataItem.getId() === dataItemId) {
						//This is the item which will have its sort set - record the priority it's at
						newPriority = isExplicitPriority ? explicitPriority : priority++;
					} else if (dataItem.getSort()) {
						//Existing item - adjust its priority
						const shift = isExplicitPriority && (priority >= explicitPriority);
						if (!dataItem.hasDefaultSort()){
							const sort = dataItem.getSort();
							sort.priority = shift ? ++priority : priority++;
							this.slotsImpl.getDataItemImpl(dataItem.getId())._setSortWithoutAdjustingPriority(sort);
						}
					}
				});
			});
			return newPriority;
		}

		_setSortWithoutAdjustingPriority(params, transactionToken) {
			return super.setSort(params, transactionToken);
		}

		setSort(params, transactionToken) {
			const sortSpec = params ? JSON.parse(JSON.stringify(params)) : {};

			if (!this.slot){
				return super.setSort(sortSpec, transactionToken);
			}
			if (sortSpec){
				// TODO - make the the setSortPriorities is abble to reset to previous prioroties if the sortSpec is not defined (i.e. clearing the sort)
				//Adjust priority setting on existing sorts prior to setting actual sort
				sortSpec.priority = this._setSortPriorities(this.slot.getId(), this.getId(), sortSpec.priority);
			}
			return super.setSort(sortSpec, transactionToken);
		}

		hasModelSort() {
			return !!this._getMetadataColumnSort();
		}

		_getMetadataColumnSort() {
			const mdColumn = this.getMetadataColumn();
			const sortType = mdColumn ? mdColumn.getDefaultSortType() : null;
			if (sortType) { // model sort found.
				// Calculate sort priority for model sort.
				let dataItemSortPriority = 0;
				let modelSortItemIdx = 0;
				let modelSortItemFound = false;
				this.slotsImpl.getDataItemImplList().forEach((dataItem) => {
					const sortObj = dataItem._getUserSort();
					if (sortObj) {
						// User defined sort is set/saved with dataitems with highest priority.
						// Priorty numbers are in ascending order with 0 been the highest priority.
						// Find the lowest priority for user defined sort.
						if (dataItemSortPriority < sortObj.priority) {
							dataItemSortPriority = sortObj.priority;
						}
					} else if (!modelSortItemFound) {
						// Find the index of the model sort data item over all dataitems with model sort but without user defned sort.
						const currentItemId = dataItem.getColumnId();
						const currentItemMdColumn = dataItem.getMetadataColumn();
						const modelSortType = currentItemMdColumn ? currentItemMdColumn.getDefaultSortType() : null;
						if (modelSortType) {
							modelSortItemIdx++;
							modelSortItemFound = currentItemId === this.getColumnId();
						}
					}
				});

				return {
					type: sortType,
					by: 'caption',
					// The current model sort priority should be the highest user defined sort priorty number plus
					// the index of the model sort data item over all dataitems with model sort but without user defned sort.
					priority: dataItemSortPriority + modelSortItemIdx
				};
			}

			return null;
		}

		_isOLAPMetadataColumn() {
			const metadataColumn = this.getMetadataColumn();
			// NamedSets are also considered like OLAP 
			return metadataColumn && (metadataColumn.isOlapColumn() || metadataColumn.isNamedSet());
		}

		/**
		 * Returns the data item sort spec by the following rules:
		 * 1. user defined sort
		 * 2. metadata column sort
		 * 3. if not olapColumn or namedSet, slot definition sort
		 * 4. no sort will return null or keep custom sort type undefined
		 * 
		 * Note: we need to check sort type because custom sort with auto option
		 *       will have sort type as undefined and we need to follow the order above
		 *       to set a value for the sort type value
		 * @returns Data item sort spec
		 * @memberof MappedDataItem
		 */
		getSort() {
			if (!this.slot){
				return super.getSort();
			}

			const userSort = super.getSort();
			if(userSort && userSort.type) {
				// Remove rankSort so that it doesn't leak out and end up being used by people
				if (userSort && userSort.rankSort) {
					delete userSort.rankSort;
				}
				return userSort;
			}

			const metadataColumnSort = this._getMetadataColumnSort();
			if(metadataColumnSort) {
				if(this._isCustomSortWithRemainingItemOrderUndefined(userSort)) {
					userSort.type = metadataColumnSort.type;
					return userSort;
				}
				return metadataColumnSort;
			}

			const slotDefinitionSortType = this.slot.getDefinition().getSort();
			if(!this._isOLAPMetadataColumn() && slotDefinitionSortType) {
				if(this._isCustomSortWithRemainingItemOrderUndefined(userSort)) {
					userSort.type = slotDefinitionSortType;
					return userSort;
				}
				return { type : slotDefinitionSortType };
			}

			return userSort;
		}

		_isCustomSortWithRemainingItemOrderUndefined(dataItemSort) {
			// we only have sort type undefined for custom sort with auto option for now
			// and we will need to visit this method if we have more undefined cases
			return dataItemSort && dataItemSort.custom && !dataItemSort.type;
		}

		//Crurrently AutobinDialog listens on BA-UI-TOOLKIT NumberInput's onChange event
		//However, there are cases that it is not the change of the number input's value that
		//caused the onChange event but just a blur or focus on number input
		//Currently onChange event that executs auto bin action, therefore, to avoid unecssary
		//auto bin action, we compare the incoming state's value with the dataitem's existing
		//binning state, we only execute auto bin action for new values
		_isSameNumberOfBins (binningSpec) {
			if(!binningSpec){
				return false;
			}
			const binningInfo = this.getBinning();
			if(!binningInfo){
				return false;
			}
			if (binningInfo.bins !== binningSpec.bins){
				return false;
			}
			return true;
		}

		setBinning(binningSpec, transactionToken) {
			if (!this.slot){
				super.setBinning(binningSpec, transactionToken);
			} else if (!this._isSameNumberOfBins(binningSpec)){
				const options = TransactionUtil.transactionTokenToOptions(transactionToken);
				this.filterSupport.clearSelectionsByDataItemIds([this.getColumnId()]);

				BinningActionsUtils.removeCompoundFilters(this.slot, this.localFilters, options);
				let binningFilters = BinningActionsUtils.getBinningFilters(this.localFilters, this);
				if (binningFilters) {
					BinningActionsUtils.removeLocalBinnedFilterEntries(this.localFilters, binningFilters, options);
				}

				super.setBinning(binningSpec, transactionToken);
			}
		}

		setFormat(spec, transactionToken) {
			const formatSpec = JSON.parse(JSON.stringify(spec));

			if (!this.slot){
				return super.setFormat(formatSpec, transactionToken);
			}

			let binningFilters;
			const options = TransactionUtil.transactionTokenToOptions(transactionToken);
			if (super.getBinning()) {
				//for any changes in binning we need clear all selection filters, it will not be part of undo/redo
				this.filterSupport.clearSelectionsByDataItemIds([super.getColumnId()]);
				BinningActionsUtils.removeCompoundFilters(this.slot, this.localFilters, options);
				binningFilters = formatSpec ? BinningActionsUtils.getBinningFilterForFormatedItem(formatSpec, this.localFilters, this) : null;
			}

			const clientSideDataFormatting = binningFilters ? false : true;

			if (binningFilters) {
				BinningActionsUtils.removeLocalBinnedFilterEntries(this.localFilters, binningFilters, options);
			}


			if (clientSideDataFormatting) {
				//Only re-send the query if needed since formatting is done on the client side so set dataIfQueryChanged to true.
				//Reasons why binned has to be formatted on the server side due to how the values are returned. Examples of binned values returned from DSS:
				//'less than 202.309000, '202.309000 to < 404.608000', '404.608000 to < 606.907000'
				//So the client side cannot format such values
				if (!options.payloadData){
					options.payloadData = {};
				}
				options.payloadData.dataIfQueryChanged = true;
			}

			// TODO livewidget_cleanup --- should be passing a transction token and not options !!
			super.setFormat(formatSpec, options);
		}

	}

	return MappedDataItem;
});
